import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, IconButton, Box, Typography, Collapse } from '@mui/material';
import { Cancel, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface PaymentResultBannerProps {
    status: 'success' | 'cancel' | null;
    paymentData?: any;
    error?: string | null;
    onDismiss?: () => void;
}

export const PaymentResultBanner: React.FC<PaymentResultBannerProps> = ({
    status,
    paymentData,
    error,
    onDismiss
}) => {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (status) {
            setShow(true);
            
            // Auto-dismiss success banner after 10 seconds
            if (status === 'success') {
                const timer = setTimeout(() => {
                    handleDismiss();
                }, 10000);
                
                return () => clearTimeout(timer);
            }
        }
    }, [status]);

    const handleDismiss = () => {
        setShow(false);
        setTimeout(() => {
            onDismiss?.();
        }, 300); // Wait for animation to complete
    };

    if (!status) return null;

    const getFeatures = (details: any) => {
        if (!details?.metadata) return [];
        
        const features = [];
        const metadata = details.metadata;

        // Handle multiple tax report purchases using indexed format (same as Firebase)
        const yearCount = parseInt(metadata.yearCount) || 0;
        if (yearCount > 0) {
            // Process each year using indexed metadata
            for (let i = 0; i < yearCount; i++) {
                const year = metadata[`taxYear_${i}`];
                const packageId = metadata[`packageId_${i}`];
                const totalTransactions = metadata[`totalTransactions_${i}`];
                
                if (packageId && year) {
                    if (totalTransactions) {
                        // Include transaction count if available
                        features.push(
                            t(`plansData.packages.${packageId}.short_description`, {
                                year: year,
                                transactions: parseInt(totalTransactions) || 0,
                            })
                        );
                    } else {
                        // Fallback to name + year format  
                        features.push(
                            t(`plansData.packages.${packageId}.name`) + ' ' + year
                        );
                    }
                }
                
                // Check expert review for this specific year
                if (metadata[`expertReview_${i}`] === 'true') {
                    features.push(t('plansData.addons.expertReview.name', 'Expert Check') + ' ' + year);
                }
            }
        }
        
        // Fallback for single tax report purchases (backward compatibility)
        else if (metadata.packageId && metadata.taxYear) {
            features.push(
                t(`plansData.packages.${metadata.packageId}.short_description`, {
                    year: metadata.taxYear,
                    transactions: parseInt(metadata.totalTransactions) || 0,
                })
            );
            
            if (metadata.expertReview === 'true') {
                features.push(
                    t('plansData.addons.expertReview.short_description', {
                        year: metadata.taxYear,
                    })
                );
            }
        }

        return features;
    };

    const renderSuccessContent = () => {
        if (error) {
            return (
                <Box>
                    <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Cancel color="error" />
                        {t('plans.payment.success.error.title', 'Error')}
                    </AlertTitle>
                    <Typography variant="body2" color="error.dark">
                        {error}
                    </Typography>
                </Box>
            );
        }

        const features = paymentData ? getFeatures(paymentData) : [];
        const featuresText = features.length > 0 ? ` ${t('plans.payment.success.purchasedItems', 'Du hast erworben:')} ${features.join(', ')}` : '';

        return (
            <Typography variant="body2">
                {t('plans.payment.success.title', 'Zahlung erfolgreich!')} {t('plans.payment.success.description', 'Dein Steuerbericht wurde freigeschaltet.')}{featuresText}
            </Typography>
        );
    };

    const renderCancelContent = () => (
        <Typography variant="body2">
            {t('cancel.description', 'Deine Zahlung wurde abgebrochen. Es wurden keine Gebühren erhoben.')}
        </Typography>
    );

    return (
        <Collapse in={show}>
            <Alert 
                severity={status === 'success' ? (error ? 'error' : 'success') : 'info'}
                sx={{
                    mb: 3,
                    '& .MuiAlert-message': {
                        width: '100%'
                    }
                }}
                action={
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={handleDismiss}
                    >
                        <Close fontSize="inherit" />
                    </IconButton>
                }
            >
                {status === 'success' ? renderSuccessContent() : renderCancelContent()}
            </Alert>
        </Collapse>
    );
};